import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Avatar,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  Fade,
  Tooltip,
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as AIIcon,
  Person as PersonIcon,
  AutoAwesome as SparkleIcon,
  Mail as MailIcon,
  Description as SummaryIcon,
  Translate as TranslateIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Refresh as RefreshIcon,
  LightbulbOutlined as IdeaIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import api from '../../utils/api';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  provider?: string;
}

const AIAssistant: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const suggestions = [
    { icon: <MailIcon fontSize="small" />, text: 'Write a professional email', color: '#2196f3' },
    { icon: <SummaryIcon fontSize="small" />, text: 'Summarize this email thread', color: '#4caf50' },
    { icon: <TranslateIcon fontSize="small" />, text: 'Translate to Spanish', color: '#ff9800' },
    { icon: <EditIcon fontSize="small" />, text: 'Improve my writing', color: '#9c27b0' },
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const apiMessages = [...messages.slice(-9), userMessage].map(m => ({
        role: m.role,
        content: m.content,
      }));

      const response = await api.post('/ai/chat', {
        messages: apiMessages,
      });

      const responseData = response.data;
      const innerData = responseData?.data?.data || responseData?.data || responseData;
      const message = innerData?.message 
        || (typeof innerData === 'string' ? innerData : null)
        || 'I apologize, but I encountered an issue. Please try again.';
      const provider = innerData?.provider || 'ai';

      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: message,
        timestamp: new Date(),
        provider: provider,
      };

      setMessages(prev => [...prev, aiResponse]);
    } catch (error: any) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `I'm sorry, I encountered an error. Please try again.\n\n*Error: ${error.response?.data?.message || error.message}*`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (text: string) => {
    setInput(text);
    inputRef.current?.focus();
  };

  const copyToClipboard = async (text: string, id: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearChat = () => {
    setMessages([]);
  };

  // Empty state with suggestions
  const EmptyState = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        px: 3,
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
        }}
      >
        <AIIcon sx={{ fontSize: 40, color: 'white' }} />
      </Box>
      
      <Typography variant="h4" fontWeight={600} gutterBottom sx={{ color: '#1a1a2e' }}>
        Hi, I'm Zia! 👋
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500 }}>
        Your intelligent email assistant. I can help you write emails, summarize threads, 
        translate messages, and much more. What would you like to do today?
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', maxWidth: 600 }}>
        {suggestions.map((suggestion, index) => (
          <Chip
            key={index}
            icon={suggestion.icon}
            label={suggestion.text}
            onClick={() => handleSuggestionClick(suggestion.text)}
            sx={{
              py: 2.5,
              px: 1,
              fontSize: '0.9rem',
              fontWeight: 500,
              bgcolor: 'white',
              border: '1px solid #e0e0e0',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: suggestion.color,
                color: 'white',
                borderColor: suggestion.color,
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 12px ${suggestion.color}40`,
                '& .MuiChip-icon': { color: 'white' },
              },
              '& .MuiChip-icon': { color: suggestion.color },
            }}
          />
        ))}
      </Box>

      <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
        <IdeaIcon fontSize="small" />
        <Typography variant="body2">
          Tip: Be specific about what you need for better results
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fafafa',
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid #e8e8e8',
          bgcolor: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.25)',
            }}
          >
            <AIIcon sx={{ fontSize: 24, color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ color: '#1a1a2e', lineHeight: 1.2 }}>
              Zia AI Assistant
            </Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>
              Powered by Llama 3.3 • Always ready to help
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {messages.length > 0 && (
            <Tooltip title="Clear chat">
              <IconButton onClick={clearChat} size="small" sx={{ color: '#666' }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Chip
            icon={<SparkleIcon sx={{ fontSize: 16 }} />}
            label="Beta"
            size="small"
            sx={{
              bgcolor: 'rgba(102, 126, 234, 0.1)',
              color: '#667eea',
              fontWeight: 600,
              '& .MuiChip-icon': { color: '#667eea' },
            }}
          />
        </Box>
      </Paper>

      {/* Messages Area */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', py: 2 }}>
        {messages.length === 0 ? (
          <EmptyState />
        ) : (
          <Box sx={{ maxWidth: 800, mx: 'auto', px: 2 }}>
            {messages.map((message) => (
              <Fade in key={message.id}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    mb: 3,
                    flexDirection: message.role === 'user' ? 'row-reverse' : 'row',
                  }}
                >
                  {/* Avatar */}
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      flexShrink: 0,
                      ...(message.role === 'assistant' 
                        ? {
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          }
                        : {
                            bgcolor: '#1976d2',
                          }
                      ),
                    }}
                  >
                    {message.role === 'assistant' ? (
                      <AIIcon sx={{ fontSize: 20, color: 'white' }} />
                    ) : (
                      <PersonIcon sx={{ fontSize: 20, color: 'white' }} />
                    )}
                  </Avatar>

                  {/* Message Content */}
                  <Box
                    sx={{
                      maxWidth: '80%',
                      minWidth: 0,
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        bgcolor: message.role === 'user' ? '#1976d2' : 'white',
                        color: message.role === 'user' ? 'white' : '#1a1a2e',
                        border: message.role === 'assistant' ? '1px solid #e8e8e8' : 'none',
                      }}
                    >
                      <Box
                        sx={{
                          '& p': { 
                            margin: 0, 
                            marginBottom: 1, 
                            '&:last-child': { marginBottom: 0 },
                            lineHeight: 1.7,
                          },
                          '& strong': { fontWeight: 600 },
                          '& ul, & ol': { 
                            margin: '12px 0', 
                            paddingLeft: '24px',
                          },
                          '& li': { 
                            marginBottom: '8px',
                            lineHeight: 1.6,
                          },
                          '& h1, & h2, & h3': {
                            marginTop: '16px',
                            marginBottom: '8px',
                            fontWeight: 600,
                          },
                          '& code': {
                            bgcolor: message.role === 'user' ? 'rgba(255,255,255,0.2)' : '#f5f5f5',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            fontFamily: '"Fira Code", "Consolas", monospace',
                            fontSize: '0.875em',
                          },
                          '& pre': {
                            bgcolor: message.role === 'user' ? 'rgba(255,255,255,0.1)' : '#f5f5f5',
                            padding: '16px',
                            borderRadius: '8px',
                            overflow: 'auto',
                            margin: '12px 0',
                          },
                          '& hr': {
                            border: 'none',
                            borderTop: '1px solid',
                            borderColor: message.role === 'user' ? 'rgba(255,255,255,0.3)' : '#e0e0e0',
                            my: 2,
                          },
                          '& blockquote': {
                            borderLeft: '3px solid #667eea',
                            paddingLeft: '16px',
                            margin: '12px 0',
                            color: '#666',
                            fontStyle: 'italic',
                          },
                          fontSize: '0.95rem',
                          lineHeight: 1.7,
                        }}
                      >
                        <ReactMarkdown>{message.content}</ReactMarkdown>
                      </Box>
                    </Paper>

                    {/* Message Meta */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mt: 0.5,
                        px: 1,
                        justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#999' }}>
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                      
                      {message.role === 'assistant' && (
                        <>
                          <Typography variant="caption" sx={{ color: '#999' }}>•</Typography>
                          <Typography variant="caption" sx={{ color: '#667eea', fontWeight: 500 }}>
                            {message.provider === 'groq' ? 'Zia' : message.provider}
                          </Typography>
                          
                          <Tooltip title={copiedId === message.id ? 'Copied!' : 'Copy'}>
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(message.content, message.id)}
                              sx={{ 
                                ml: 0.5,
                                p: 0.5,
                                color: copiedId === message.id ? '#4caf50' : '#999',
                                '&:hover': { color: '#667eea' },
                              }}
                            >
                              {copiedId === message.id ? (
                                <CheckIcon sx={{ fontSize: 16 }} />
                              ) : (
                                <CopyIcon sx={{ fontSize: 16 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Fade>
            ))}

            {/* Loading Indicator */}
            {isLoading && (
              <Fade in>
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    }}
                  >
                    <AIIcon sx={{ fontSize: 20, color: 'white' }} />
                  </Avatar>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      bgcolor: 'white',
                      border: '1px solid #e8e8e8',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {[0, 1, 2].map((i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: '#667eea',
                            animation: 'pulse 1.4s ease-in-out infinite',
                            animationDelay: `${i * 0.2}s`,
                            '@keyframes pulse': {
                              '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                              '40%': { opacity: 1, transform: 'scale(1)' },
                            },
                          }}
                        />
                      ))}
                    </Box>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      Zia is thinking...
                    </Typography>
                  </Paper>
                </Box>
              </Fade>
            )}

            <div ref={messagesEndRef} />
          </Box>
        )}
      </Box>

      {/* Input Area */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderTop: '1px solid #e8e8e8',
          bgcolor: 'white',
        }}
      >
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Message Zia..."
            inputRef={inputRef}
            disabled={isLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                bgcolor: '#f5f5f5',
                transition: 'all 0.2s ease',
                '& fieldset': {
                  borderColor: 'transparent',
                },
                '&:hover fieldset': {
                  borderColor: '#e0e0e0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#667eea',
                  borderWidth: 2,
                },
                '&.Mui-focused': {
                  bgcolor: 'white',
                  boxShadow: '0 0 0 4px rgba(102, 126, 234, 0.1)',
                },
              },
              '& .MuiInputBase-input': {
                py: 1.5,
                fontSize: '0.95rem',
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleSend}
                    disabled={!input.trim() || isLoading}
                    sx={{
                      bgcolor: input.trim() ? '#667eea' : 'transparent',
                      color: input.trim() ? 'white' : '#999',
                      '&:hover': {
                        bgcolor: input.trim() ? '#5a6fd6' : 'transparent',
                      },
                      '&.Mui-disabled': {
                        bgcolor: 'transparent',
                        color: '#ccc',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SendIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 1,
              color: '#999',
            }}
          >
            Zia may produce inaccurate information. Always review before sending.
          </Typography>
        </Box>
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AIAssistant;
